package com.writerslab.office

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.files.File

private sealed class BlogScreen {
    object List : BlogScreen()
    data class Edit(val id: Long?, val seedTranslationKey: String? = null, val seedLanguage: String? = null) : BlogScreen()
}

private val LANGUAGES = listOf("ru", "en")
private fun langName(code: String) = if (code == "en") "English" else "Русский"

@Composable
fun BlogPage() {
    var screen by remember { mutableStateOf<BlogScreen>(BlogScreen.List) }
    when (val s = screen) {
        is BlogScreen.List -> BlogListView(
            onNew = { screen = BlogScreen.Edit(null) },
            onEdit = { id -> screen = BlogScreen.Edit(id) },
        )
        is BlogScreen.Edit -> BlogEditView(
            id = s.id,
            seedTranslationKey = s.seedTranslationKey,
            seedLanguage = s.seedLanguage,
            onDone = { screen = BlogScreen.List },
            onOpen = { id -> screen = BlogScreen.Edit(id) },
            onAddTranslation = { key, lang -> screen = BlogScreen.Edit(null, key, lang) },
        )
    }
}

// ── List ────────────────────────────────────────────────────────────────

@Composable
private fun BlogListView(onNew: () -> Unit, onEdit: (Long) -> Unit) {
    var items by remember { mutableStateOf<List<BlogSummary>?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun load() {
        scope.launch {
            error = null; items = null
            if (PREVIEW) { items = MockApi.blogList(); return@launch }
            try { items = ApiClient.getBlogList() }
            catch (e: Throwable) { error = e.message ?: "Ошибка загрузки" }
        }
    }
    LaunchedEffect(Unit) { load() }

    Div({ style { property("padding", "24px 0") } }) {
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "12px"); marginBottom(20.px) } }) {
            H2({ style { color(Color(C.OnSurface)); property("font-size", "18px"); margin(0.px) } }) { Text("Блог") }
            SmallButton("Новая статья") { onNew() }
            SmallButton("Обновить") { load() }
        }

        if (error != null) { P({ style { color(Color(C.Error)) } }) { Text(error!!) }; return@Div }
        if (items == null) { P({ style { color(Color(C.Muted)) } }) { Text("Загрузка…") }; return@Div }
        if (items!!.isEmpty()) { P({ style { color(Color(C.Muted)) } }) { Text("Пока нет статей. Создайте первую.") }; return@Div }

        Div({ style { property("overflow-x", "auto") } }) {
            Table({ style { tableStyle() } }) {
                Thead {
                    Tr {
                        Th { Text("Заголовок") }
                        Th { Text("Язык") }
                        Th { Text("Статус") }
                        Th { Text("👁 / 📖") }
                        Th { Text("👍 / 👎") }
                        Th { Text("Обновлено") }
                        Th { Text("Действия") }
                    }
                }
                Tbody {
                    items!!.forEach { a -> BlogRow(a, onEdit) { load() } }
                }
            }
        }
    }
}

@Composable
private fun BlogRow(a: BlogSummary, onEdit: (Long) -> Unit, reload: () -> Unit) {
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    Tr {
        Td {
            Span({ style { color(Color(C.OnSurface)); property("font-weight", "600") } }) { Text(a.title) }
            Br()
            Span({ style { mono(); fontSize(11.px); color(Color(C.Muted)) } }) { Text("/blog/${a.slug}") }
            if (error != null) { Br(); Span({ style { color(Color(C.Error)); fontSize(11.px) } }) { Text(error!!) } }
        }
        Td { Text(a.language.uppercase()) }
        Td { StatusBadge(a.status) }
        Td { Text("${a.views} / ${a.reads}") }
        Td { Text("${a.likes} / ${a.dislikes}") }
        Td { Text(a.updatedAt.take(10)) }
        Td {
            Div({ style { property("display", "flex"); property("gap", "6px"); property("flex-wrap", "wrap") } }) {
                SmallButton("Открыть") { onEdit(a.id) }
                val published = a.status == "PUBLISHED"
                SmallButton(if (published) "Скрыть" else "Опубликовать") {
                    scope.launch {
                        error = null
                        try {
                            if (published) ApiClient.hideBlog(a.id) else ApiClient.publishBlog(a.id)
                            reload()
                        } catch (e: Throwable) { error = e.message }
                    }
                }
                SmallButton("Удалить") {
                    if (window.confirm("Удалить «${a.title}»? Действие необратимо.")) {
                        scope.launch {
                            error = null
                            try { ApiClient.deleteBlog(a.id); reload() }
                            catch (e: Throwable) { error = e.message }
                        }
                    }
                }
            }
        }
    }
}

// ── Editor ──────────────────────────────────────────────────────────────

@Composable
private fun BlogEditView(
    id: Long?,
    seedTranslationKey: String?,
    seedLanguage: String?,
    onDone: () -> Unit,
    onOpen: (Long) -> Unit,
    onAddTranslation: (String, String) -> Unit,
) {
    var currentId by remember { mutableStateOf(id) }
    var loaded by remember { mutableStateOf(id == null) }
    var loadError by remember { mutableStateOf<String?>(null) }

    var title by remember { mutableStateOf("") }
    var slug by remember { mutableStateOf("") }
    var author by remember { mutableStateOf("") }
    var language by remember { mutableStateOf(seedLanguage ?: "ru") }
    var translationKey by remember { mutableStateOf(seedTranslationKey) }
    var translations by remember { mutableStateOf<List<TranslationRef>>(emptyList()) }
    var excerpt by remember { mutableStateOf("") }
    var content by remember { mutableStateOf("") }
    var coverPath by remember { mutableStateOf("") }
    var coverAlt by remember { mutableStateOf("") }
    var metaTitle by remember { mutableStateOf("") }
    var metaDescription by remember { mutableStateOf("") }
    var status by remember { mutableStateOf("DRAFT") }
    var likes by remember { mutableStateOf(0) }
    var dislikes by remember { mutableStateOf(0) }
    var views by remember { mutableStateOf(0) }
    var reads by remember { mutableStateOf(0) }

    // A translation's language is fixed to the seed; a fresh article is editable.
    val languageLocked = seedTranslationKey != null

    var saving by remember { mutableStateOf(false) }
    var uploading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var savedNote by remember { mutableStateOf<String?>(null) }

    var taRef by remember { mutableStateOf<HTMLTextAreaElement?>(null) }
    val scope = rememberCoroutineScope()

    fun apply(a: BlogArticle) {
        title = a.title; slug = a.slug; author = a.authorName; language = a.language
        translationKey = a.translationKey; translations = a.translations
        excerpt = a.excerpt ?: ""; content = a.contentMarkdown
        coverPath = a.coverImagePath ?: ""; coverAlt = a.coverImageAlt ?: ""
        metaTitle = a.metaTitle ?: ""; metaDescription = a.metaDescription ?: ""
        status = a.status; likes = a.likes; dislikes = a.dislikes
        views = a.views; reads = a.reads; currentId = a.id
    }

    LaunchedEffect(id) {
        if (id == null) return@LaunchedEffect
        try {
            val a = if (PREVIEW) MockApi.blogArticle(id) else ApiClient.getBlogArticle(id)
            apply(a); loaded = true
        } catch (e: Throwable) { loadError = e.message ?: "Ошибка загрузки" }
    }

    fun buildReq() = BlogUpsertRequest(
        title = title,
        slug = slug.ifBlank { null },
        language = language,
        translationKey = translationKey,
        excerpt = excerpt.ifBlank { null },
        contentMarkdown = content,
        coverImagePath = coverPath.ifBlank { null },
        coverImageAlt = coverAlt.ifBlank { null },
        metaTitle = metaTitle.ifBlank { null },
        metaDescription = metaDescription.ifBlank { null },
        authorName = author.ifBlank { null },
    )

    fun save(then: (() -> Unit)? = null) {
        if (saving) return  // synchronous guard: a double Save / Ctrl+S must not create two articles
        if (title.isBlank()) { error = "Заголовок обязателен"; return }
        saving = true
        scope.launch {
            error = null; savedNote = null
            try {
                val saved = if (currentId == null) ApiClient.createBlog(buildReq())
                            else ApiClient.updateBlog(currentId!!, buildReq())
                apply(saved); savedNote = "Сохранено"; then?.invoke()
            } catch (e: Throwable) { error = e.message ?: "Ошибка сохранения" }
            finally { saving = false }
        }
    }

    fun changeStatus(publish: Boolean) {
        val cid = currentId ?: return
        if (saving) return
        saving = true
        scope.launch {
            error = null
            try {
                val saved = if (publish) ApiClient.publishBlog(cid) else ApiClient.hideBlog(cid)
                apply(saved); savedNote = if (publish) "Опубликовано" else "Скрыто"
            } catch (e: Throwable) { error = e.message } finally { saving = false }
        }
    }

    // Markdown toolbar helpers. We mutate the DOM textarea FIRST, then sync
    // `content`: because the controlled TextArea's value then already equals
    // `content`, Compose does not re-write it and the caret/scroll stays put
    // (the old approach let Compose reset value → caret jumped to the end).
    fun applyEdit(newContent: String, selStart: Int, selEnd: Int) {
        val el = taRef
        if (el != null) {
            el.value = newContent
            el.setSelectionRange(selStart, selEnd)
            el.focus()
        }
        content = newContent
        if (el != null) window.requestAnimationFrame {
            el.setSelectionRange(selStart, selEnd)
        }
    }
    // Toggle: wrap the selection with the markers, or unwrap if already wrapped
    // (so pressing the same button / shortcut twice undoes it).
    fun wrap(before: String, after: String) {
        val el = taRef ?: return
        val start = el.selectionStart ?: content.length
        val end = el.selectionEnd ?: content.length
        val sel = content.substring(start, end)
        // Markers are part of the selection.
        if (sel.length >= before.length + after.length && sel.startsWith(before) && sel.endsWith(after)) {
            val inner = sel.substring(before.length, sel.length - after.length)
            applyEdit(content.substring(0, start) + inner + content.substring(end), start, start + inner.length)
            return
        }
        // Markers sit just outside the selection.
        val outerStart = start - before.length
        val outerEnd = end + after.length
        if (outerStart >= 0 && outerEnd <= content.length &&
            content.substring(outerStart, start) == before && content.substring(end, outerEnd) == after) {
            applyEdit(content.substring(0, outerStart) + sel + content.substring(outerEnd), outerStart, outerStart + sel.length)
            return
        }
        val newContent = content.substring(0, start) + before + sel + after + content.substring(end)
        val innerStart = start + before.length
        applyEdit(newContent, innerStart, innerStart + sel.length)
    }
    fun lineStartOf(pos: Int): Int =
        if (pos == 0) 0 else content.lastIndexOf('\n', pos - 1).let { if (it < 0) 0 else it + 1 }
    fun lineEndOf(pos: Int): Int = content.indexOf('\n', pos).let { if (it < 0) content.length else it }

    // Line-prefix toggle for lists / quote / checklist, across ALL selected
    // lines (add to each; remove if every non-blank line already has it).
    fun linePrefix(prefix: String) {
        val el = taRef ?: return
        val selStart = el.selectionStart ?: content.length
        val selEnd = el.selectionEnd ?: selStart
        val blockStart = lineStartOf(selStart)
        val blockEnd = lineEndOf(if (selEnd > selStart) selEnd - 1 else selEnd)
        val lines = content.substring(blockStart, blockEnd).split('\n')
        val allHave = lines.all { it.isBlank() || it.startsWith(prefix) }
        val newBlock = lines.joinToString("\n") { line ->
            when {
                line.isBlank() -> line
                allHave -> line.removePrefix(prefix)
                else -> prefix + line
            }
        }
        applyEdit(content.substring(0, blockStart) + newBlock + content.substring(blockEnd), blockStart, blockStart + newBlock.length)
    }

    // Heading toggle: replaces any existing heading level on the line, or
    // removes it if the same level is applied again (so H2 on "# t" → "## t",
    // and H2 again → "t"). `prefix` is like "## ".
    fun headingPrefix(prefix: String) {
        val el = taRef ?: return
        val pos = el.selectionStart ?: content.length
        val ls = lineStartOf(pos)
        val le = lineEndOf(ls)
        val line = content.substring(ls, le)
        val existing = Regex("^#{1,6} ").find(line)?.value
        val body = if (existing != null) line.substring(existing.length) else line
        val newLine = if (existing == prefix) body else prefix + body
        val caret = (pos + (newLine.length - line.length)).coerceAtLeast(ls)
        applyEdit(content.substring(0, ls) + newLine + content.substring(le), caret, caret)
    }
    fun insertAtCaret(text: String) {
        val pos = taRef?.selectionStart ?: content.length
        val newContent = content.substring(0, pos) + text + content.substring(pos)
        val caret = pos + text.length
        applyEdit(newContent, caret, caret)
    }
    fun uploadInline(file: File) {
        scope.launch {
            uploading = true; error = null
            try {
                val url = ApiClient.uploadBlogImage(file).url
                val w = window.prompt("Ширина картинки в пикселях (пусто — на всю ширину):", "")?.trim()
                val md = if (!w.isNullOrEmpty() && w.all { it.isDigit() }) "\n![](${url} =${w}x)\n" else "\n![](${url})\n"
                insertAtCaret(md)
            } catch (e: Throwable) { error = e.message ?: "Не удалось загрузить" } finally { uploading = false }
        }
    }
    // Uploads in order, then either appends the slides to the carousel the
    // caret is in, or inserts a new ```carousel block at the caret — so the
    // author never has to type the block syntax by hand.
    fun uploadCarousel(files: List<File>) {
        scope.launch {
            uploading = true; error = null
            try {
                val lines = files.map { "![](${ApiClient.uploadBlogImage(it).url})" }
                val caret = taRef?.selectionStart ?: content.length
                val closing = carouselClosingFenceAt(content, caret)
                if (closing != null) {
                    val text = lines.joinToString("") { "$it\n" }
                    applyEdit(content.substring(0, closing) + text + content.substring(closing), closing + text.length, closing + text.length)
                } else {
                    insertAtCaret("\n\n```carousel\n" + lines.joinToString("\n") + "\n```\n\n")
                }
            } catch (e: Throwable) { error = e.message ?: "Не удалось загрузить" } finally { uploading = false }
        }
    }
    fun uploadCover(file: File) {
        scope.launch {
            uploading = true; error = null
            try { coverPath = ApiClient.uploadBlogImage(file).path }
            catch (e: Throwable) { error = e.message ?: "Не удалось загрузить" } finally { uploading = false }
        }
    }

    if (loadError != null) {
        Div({ style { property("padding", "24px 0") } }) {
            SmallButton("← Назад") { onDone() }
            P({ style { color(Color(C.Error)) } }) { Text(loadError!!) }
        }
        return
    }
    if (!loaded) {
        Div({ style { property("padding", "24px 0") } }) { P({ style { color(Color(C.Muted)) } }) { Text("Загрузка…") } }
        return
    }

    Div({ style { property("padding", "20px 0") } }) {
        // Header
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "12px"); property("flex-wrap", "wrap"); marginBottom(18.px) } }) {
            SmallButton("← К списку") { onDone() }
            H2({ style { color(Color(C.OnSurface)); property("font-size", "18px"); margin(0.px) } }) {
                Text(when {
                    currentId != null -> "Редактирование"
                    languageLocked -> "Новый перевод (${language.uppercase()})"
                    else -> "Новая статья"
                })
            }
            StatusBadge(status)
            if (currentId != null) {
                Span({ style { color(Color(C.Muted)); fontSize(13.px) } }) {
                    Text("👁 $views · 📖 $reads · 👍 $likes · 👎 $dislikes")
                }
            }
            Div({ style { property("flex", "1") } })
            if (savedNote != null) Span({ style { color(Color(C.Success)); fontSize(13.px) } }) { Text(savedNote!!) }
            if (error != null) Span({ style { color(Color(C.Error)); fontSize(13.px) } }) { Text(error!!) }
            Button({ style { btn() }; if (saving) attr("disabled", ""); onClick { save() } }) {
                Text(if (saving) "Сохранение…" else "Сохранить")
            }
            if (currentId != null) {
                val published = status == "PUBLISHED"
                Button({
                    style { btn(if (published) C.SurfaceHigh else C.Success, if (published) C.OnSurface else "#15131A") }
                    if (saving) attr("disabled", "")
                    onClick { changeStatus(!published) }
                }) { Text(if (published) "Скрыть" else "Опубликовать") }
            }
        }

        // Meta fields
        Div({ style { card(); marginBottom(14.px); property("display", "flex"); property("flex-direction", "column"); property("gap", "10px") } }) {
            // Language
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "4px") } }) {
                Span({ style { fontSize(12.px); color(Color(C.Muted)) } }) { Text("Язык статьи") }
                Div({ style { property("display", "flex"); property("gap", "6px") } }) {
                    LANGUAGES.forEach { code ->
                        val active = language == code
                        Button({
                            style {
                                btn(if (active) C.Primary else C.SurfaceHigh, if (active) "#15131A" else C.OnSurface)
                                property("border", "1px solid ${C.Border}")
                            }
                            if (languageLocked) attr("disabled", "")
                            onClick { if (!languageLocked) language = code }
                        }) { Text(langName(code)) }
                    }
                    if (languageLocked) Span({ style { color(Color(C.Muted)); fontSize(12.px); property("align-self", "center") } }) {
                        Text("язык перевода зафиксирован")
                    }
                }
            }
            EditorField("Заголовок") { TextInput(title) { onInput { title = it.value }; style { fieldStyle() } } }
            EditorField("Slug (URL, необязательно — сгенерируется из заголовка; уникален для всего блога)") {
                TextInput(slug) { onInput { slug = it.value }; style { fieldStyle() }; attr("placeholder", "kak-pisat-dialogi") }
            }
            EditorField("Автор") { TextInput(author) { onInput { author = it.value }; style { fieldStyle() } } }
            EditorField("Краткое описание (excerpt — тизер в списке и в соцсетях)") {
                TextArea(excerpt) { onInput { excerpt = it.value }; style { fieldStyle(); property("min-height", "54px"); property("resize", "vertical") } }
            }
            // Cover
            Div({ style { property("display", "flex"); property("gap", "12px"); property("align-items", "flex-start"); property("flex-wrap", "wrap") } }) {
                if (coverPath.isNotBlank()) {
                    Img(src = "${Auth.apiBase}/blog/media/$coverPath", alt = "cover") {
                        attr("style", "width:120px;height:80px;object-fit:cover;border-radius:6px;border:1px solid ${C.Border}")
                    }
                }
                Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "6px") } }) {
                    Span({ style { label() } }) { Text("Обложка") }
                    FilePicker("Загрузить обложку") { uploadCover(it.first()) }
                    if (coverPath.isNotBlank()) {
                        TextInput(coverAlt) { onInput { coverAlt = it.value }; style { fieldStyle() }; attr("placeholder", "alt-текст обложки") }
                        SmallButton("Убрать обложку") { coverPath = ""; coverAlt = "" }
                    }
                }
            }
        }

        // Translations panel
        if (currentId != null) {
            TranslationsPanel(language, translations, onOpen) { lang -> onAddTranslation(translationKey ?: "", lang) }
        }

        // Editor + preview
        Div({ style { property("display", "grid"); property("grid-template-columns", "1fr 1fr"); property("gap", "12px") } }) {
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "6px"); property("min-width", "0") } }) {
                Div({ style { property("display", "flex"); property("gap", "4px"); property("flex-wrap", "wrap"); property("align-items", "center") } }) {
                    ToolBtn("H1", "Заголовок 1") { headingPrefix("# ") }
                    ToolBtn("H2", "Заголовок 2") { headingPrefix("## ") }
                    ToolBtn("H3", "Заголовок 3") { headingPrefix("### ") }
                    ToolSep()
                    ToolBtn("Ж", "Жирный") { wrap("**", "**") }
                    ToolBtn("К", "Курсив") { wrap("_", "_") }
                    ToolBtn("S", "Зачёркнутый") { wrap("~~", "~~") }
                    ToolSep()
                    ToolBtn("•", "Маркированный список") { linePrefix("- ") }
                    ToolBtn("1.", "Нумерованный список") { linePrefix("1. ") }
                    ToolBtn("☑", "Чек-лист") { linePrefix("- [ ] ") }
                    ToolBtn("„", "Цитата") { linePrefix("> ") }
                    ToolSep()
                    ToolBtn("</>", "Строчный код (Ctrl+E)") { wrap("`", "`") }
                    ToolBtn("{ }", "Блок кода") { wrap("\n```\n", "\n```\n") }
                    ToolBtn("⊞", "Таблица") { insertAtCaret("\n| Заголовок | Заголовок |\n| --- | --- |\n| Ячейка | Ячейка |\n") }
                    ToolBtn("—", "Разделитель") { insertAtCaret("\n\n---\n\n") }
                    ToolBtn("🔗", "Ссылка (Ctrl+K)") { wrap("[", "](https://)") }
                    FilePicker(if (uploading) "Загрузка…" else "🖼 Картинка") { uploadInline(it.first()) }
                    FilePicker(
                        if (uploading) "Загрузка…" else "🎞 Карусель",
                        multiple = true,
                        title = "Выберите несколько картинок — они станут листаемой каруселью. " +
                            "Если курсор стоит внутри карусели, картинки добавятся в неё.",
                    ) { uploadCarousel(it) }
                }
                TextArea(content) {
                    ref { el -> taRef = el; onDispose { taRef = null } }
                    onInput { content = it.value }
                    onKeyDown { e ->
                        if ((e.ctrlKey || e.metaKey) && !e.altKey) {
                            // Match by physical key code so shortcuts work on a
                            // Cyrillic layout too (where e.key would be "и" etc).
                            when (e.code) {
                                "KeyB" -> { e.preventDefault(); wrap("**", "**") }
                                "KeyI" -> { e.preventDefault(); wrap("_", "_") }
                                "KeyK" -> { e.preventDefault(); wrap("[", "](https://)") }
                                "KeyE" -> { e.preventDefault(); wrap("`", "`") }
                                "KeyS" -> { e.preventDefault(); save() }
                            }
                        }
                    }
                    style {
                        fieldStyle()
                        property("height", "72vh"); property("min-height", "480px"); property("resize", "vertical")
                        property("font-family", "'Courier New', monospace"); property("font-size", "14px"); property("line-height", "1.65")
                    }
                    attr("placeholder", "Пишите статью в Markdown. Кнопки и Ctrl+B/I/K вставляют разметку.")
                }
            }
            Div({ style { property("min-width", "0") } }) {
                var previewFrame by remember { mutableStateOf<HTMLIFrameElement?>(null) }
                var previewError by remember { mutableStateOf<String?>(null) }
                Div({ style { property("display", "flex"); property("gap", "8px"); marginBottom(6.px) } }) {
                    Span({ style { label() } }) { Text("Предпросмотр") }
                    if (previewError != null) Span({ style { color(Color(C.Error)); fontSize(12.px) } }) { Text(previewError!!) }
                }
                Iframe({
                    ref { el -> previewFrame = el; onDispose { previewFrame = null } }
                    attr("title", "Предпросмотр статьи")
                    style {
                        width(100.percent); property("height", "72vh"); property("min-height", "480px")
                        property("border", "1px solid ${C.Border}"); borderRadius(8.px)
                        property("display", "block"); property("box-sizing", "border-box")
                    }
                })
                // Rendered by the backend with the published article's renderer,
                // CSS and carousel script, so what you see is what readers get.
                LaunchedEffect(content, language, previewFrame) {
                    val frame = previewFrame ?: return@LaunchedEffect
                    kotlinx.coroutines.delay(400)  // debounce: a round-trip per pause, not per keystroke
                    val page = if (PREVIEW) PREVIEW_UNAVAILABLE else try {
                        ApiClient.previewBlog(content, language).also { previewError = null }
                    } catch (e: Throwable) {
                        if (e is kotlinx.coroutines.CancellationException) throw e
                        previewError = e.message ?: "Ошибка предпросмотра"
                        return@LaunchedEffect
                    }
                    showPreview(frame, page)
                }
            }
        }

        // SEO overrides
        Div({ style { card(); marginTop(14.px); property("display", "flex"); property("flex-direction", "column"); property("gap", "10px") } }) {
            Span({ style { label() } }) { Text("SEO (необязательно — по умолчанию берётся из заголовка и описания)") }
            EditorField("Meta title") { TextInput(metaTitle) { onInput { metaTitle = it.value }; style { fieldStyle() } } }
            EditorField("Meta description") {
                TextArea(metaDescription) { onInput { metaDescription = it.value }; style { fieldStyle(); property("min-height", "48px"); property("resize", "vertical") } }
            }
        }
    }
}

// ── Translations panel ──────────────────────────────────────────────────

@Composable
private fun TranslationsPanel(
    currentLang: String,
    translations: List<TranslationRef>,
    onOpen: (Long) -> Unit,
    onAdd: (String) -> Unit,
) {
    val existing = (translations.map { it.language } + currentLang).toSet()
    val missing = LANGUAGES.filter { it !in existing }
    Div({ style { card(); marginBottom(14.px); property("display", "flex"); property("flex-direction", "column"); property("gap", "8px") } }) {
        Span({ style { label() } }) { Text("Переводы") }
        Div({ style { property("display", "flex"); property("gap", "8px"); property("flex-wrap", "wrap"); property("align-items", "center") } }) {
            Span({ style { fontSize(13.px); color(Color(C.OnSurface)) } }) { Text("${langName(currentLang)} (эта статья)") }
            translations.forEach { t ->
                SmallButton("${t.language.uppercase()}: ${t.title.take(24)}") { onOpen(t.id) }
            }
            missing.forEach { lang ->
                SmallButton("+ Перевод на ${langName(lang)}") { onAdd(lang) }
            }
        }
        if (missing.isNotEmpty()) {
            Span({ style { color(Color(C.Muted)); fontSize(12.px) } }) {
                Text("Перевод создаётся как отдельная статья, связанная с этой.")
            }
        }
    }
}

// ── Small components ────────────────────────────────────────────────────

@Composable
private fun StatusBadge(status: String) {
    val (text, color, bg) = when (status) {
        "PUBLISHED" -> Triple("Опубликовано", C.Success, "rgba(111,207,151,0.15)")
        "HIDDEN" -> Triple("Скрыто", C.Warning, "rgba(242,201,76,0.15)")
        else -> Triple("Черновик", C.Muted, "rgba(154,148,168,0.15)")
    }
    Span({
        style {
            fontSize(11.px); property("font-weight", "700"); property("text-transform", "uppercase")
            property("letter-spacing", "0.5px"); padding(3.px, 8.px); borderRadius(999.px)
            backgroundColor(Color(bg)); color(Color(color))
        }
    }) { Text(text) }
}

@Composable
private fun EditorField(labelText: String, content: @Composable () -> Unit) {
    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "4px") } }) {
        Span({ style { fontSize(12.px); color(Color(C.Muted)) } }) { Text(labelText) }
        content()
    }
}

@Composable
private fun ToolBtn(glyph: String, title: String, onClick: () -> Unit) {
    Button({
        style {
            backgroundColor(Color(C.SurfaceHigh)); color(Color(C.OnSurface))
            property("border", "1px solid ${C.Border}"); borderRadius(6.px)
            property("min-width", "30px"); property("height", "30px"); padding(0.px, 8.px)
            cursor("pointer"); fontSize(13.px)
        }
        attr("title", title); attr("type", "button")
        onClick { onClick() }
    }) { Text(glyph) }
}

@Composable
private fun ToolSep() {
    Div({ style { property("width", "1px"); property("height", "20px"); property("background-color", C.Border); property("margin", "0 3px") } })
}

@Composable
private fun FilePicker(label: String, multiple: Boolean = false, title: String? = null, onPick: (List<File>) -> Unit) {
    var input by remember { mutableStateOf<HTMLInputElement?>(null) }
    Button({
        style {
            backgroundColor(Color(C.SurfaceHigh)); color(Color(C.OnSurface))
            property("border", "1px solid ${C.Border}"); borderRadius(6.px)
            padding(6.px, 12.px); cursor("pointer"); fontSize(13.px)
        }
        attr("type", "button")
        if (title != null) attr("title", title)
        onClick { input?.click() }
    }) { Text(label) }
    Input(type = InputType.File) {
        ref { el -> input = el; onDispose { input = null } }
        attr("accept", "image/png,image/jpeg,image/webp,image/gif")
        if (multiple) attr("multiple", "")
        style { display(DisplayStyle.None) }
        onChange {
            val list = input?.files ?: return@onChange
            val files = (0 until list.length).mapNotNull { list.item(it) }
            if (files.isNotEmpty()) { onPick(files); input?.value = "" }
        }
    }
}

private fun StyleScope.fieldStyle() {
    width(100.percent)
    padding(7.px, 10.px)
    backgroundColor(Color(C.SurfaceHigh))
    color(Color(C.OnSurface))
    property("border", "1px solid ${C.Border}")
    borderRadius(6.px)
    property("font-size", "13px")
    property("outline", "none")
    property("box-sizing", "border-box")
}

/**
 * Swaps the iframe's document, keeping the reader's scroll position — every
 * refresh is a fresh document, which would otherwise jump back to the top.
 * Media paths are pointed at the API origin (differs from the office origin
 * in local dev; a no-op in prod where both share an origin).
 */
private fun showPreview(frame: HTMLIFrameElement, page: String) {
    val y = runCatching { frame.contentWindow?.scrollY }.getOrNull() ?: 0.0
    frame.onload = { frame.contentWindow?.scrollTo(0.0, y) }
    frame.srcdoc = page.replace("\"/blog/media/", "\"${Auth.apiBase}/blog/media/")
}

/**
 * If [pos] is inside a ```carousel fenced block, the offset of that block's
 * closing fence line (where new slides are appended); otherwise null.
 */
private fun carouselClosingFenceAt(content: String, pos: Int): Int? {
    val fence = Regex("^ {0,3}(`{3,}|~{3,})(.*)$")
    var offset = 0
    var open: String? = null
    var openIsCarousel = false
    var openStart = 0
    for (line in content.split('\n')) {
        val m = fence.matchEntire(line)
        val current = open
        if (current == null) {
            if (m != null) {
                open = m.groupValues[1]
                openIsCarousel = m.groupValues[2].trim().substringBefore(' ').lowercase() == "carousel"
                openStart = offset
            }
        } else if (m != null && m.groupValues[2].isBlank() &&
            m.groupValues[1][0] == current[0] && m.groupValues[1].length >= current.length) {
            if (openIsCarousel && pos > openStart && pos <= offset + line.length) return offset
            open = null
        }
        offset += line.length + 1
    }
    return null
}

private const val PREVIEW_UNAVAILABLE =
    "<body style=\"font-family:system-ui;color:#9A94A8;background:#15131A;padding:16px\">" +
        "Предпросмотр рендерится бэкендом и недоступен в режиме макета.</body>"
